import type { Campground } from "@/types/api";

/**
 * Gets the country label by country code from the countries record
 * @param countryCode - The country code to look up
 * @param countries - Record mapping country codes to labels
 * @returns The country label or empty string if not found
 */
export function getCountryByCode(
  countryCode: string | undefined | null,
  countries: Record<string, string>,
): string {
  if (!countryCode) {
    return "";
  }
  return countries[countryCode] || "";
}

/**
 * Formats a campground address into a single string
 * Format: street1 street2 zip_code city country
 * @param campground - The campground object with address fields
 * @param countries - Record mapping country codes to labels for country lookup
 * @returns Formatted address string
 */
export function formatCampgroundAddress(
  campground: Campground | null | undefined,
  countries: Record<string, string>,
): string {
  if (!campground) {
    return "";
  }

  const addressParts: string[] = [];

  if (campground.street1) {
    addressParts.push(campground.street1);
  }

  if (campground.street2) {
    addressParts.push(campground.street2);
  }

  if (campground.city) {
    const zipCode = campground.zip_code ? `${campground.zip_code} ` : "";
    addressParts.push(`${zipCode}${campground.city}`);
  }

  if (campground.country) {
    const countryLabel = getCountryByCode(campground.country, countries);
    if (countryLabel) {
      addressParts.push(countryLabel);
    }
  }

  return addressParts.join("\n");
}
